import { type AnimationAction, type Bone, type Skeleton, type SkinnedMesh } from 'three'
import { type TransformControls } from 'three/examples/jsm/controls/TransformControls.js'

export type PoseModeBoneSelectionListener = (bone: Bone | null) => void

/**
 * Manages "Pose Mode" inside the AnimationsListing step:
 *  - pauses the animation actions so the mixer doesn't overwrite the user's edits
 *  - enables the global `TransformControls` (already attached to camera+renderer
 *    by `Mesh2MotionEngine`) and forces it to rotate-only
 *  - exposes the active skeleton so the engine's mousedown handler knows which
 *    bones to raycast against in this step
 *
 * Lifecycle: the controller is constructed once per `StepAnimationsListing` and
 * given references to the global `TransformControls`. Per playback session the
 * caller updates `set_active(skinned_mesh, actions)` so the controller knows
 * which skeleton to expose. Toggling on/off is idempotent.
 */
export class PoseModeController {
  private readonly transform_controls: TransformControls
  private active_skinned_mesh: SkinnedMesh | null = null
  private active_actions: AnimationAction[] = []
  private was_playing_before_enter: boolean = false
  private active: boolean = false
  private selected_bone: Bone | null = null
  private readonly selection_listeners: PoseModeBoneSelectionListener[] = []

  constructor (transform_controls: TransformControls) {
    this.transform_controls = transform_controls
  }

  /**
   * Subscribe to bone-selection changes. Listeners receive the new bone (or `null`
   * if cleared). Called every time `select_bone` runs — both viewport-click and
   * picker-panel paths funnel through it, so listeners only have to register once.
   */
  add_selection_listener (listener: PoseModeBoneSelectionListener): void {
    this.selection_listeners.push(listener)
  }

  /**
   * Single source of truth for "this bone is now the active gizmo target". Both
   * the viewport-click path (Mesh2MotionEngine.handle_pose_mode_mouse_down) and the
   * bone-picker panel call this so any side-effect listener (e.g. weight
   * highlighting) fires uniformly.
   */
  select_bone (bone: Bone | null): void {
    this.selected_bone = bone
    if (bone === null) {
      this.transform_controls.detach()
    } else {
      this.transform_controls.attach(bone)
    }
    for (const listener of this.selection_listeners) listener(bone)
  }

  get_selected_bone (): Bone | null {
    return this.selected_bone
  }

  /** Tells the controller which skinned mesh + actions are currently driving the viewport. */
  set_active_targets (skinned_mesh: SkinnedMesh | null, actions: AnimationAction[]): void {
    this.active_skinned_mesh = skinned_mesh
    this.active_actions = actions
  }

  is_active (): boolean {
    return this.active
  }

  /**
   * Returns the skeleton the engine should raycast against when dispatching a
   * pose-mode mousedown. Null while pose mode is off or no mesh is set.
   */
  get_skeleton_for_raycast (): Skeleton | null {
    if (!this.active || this.active_skinned_mesh === null) return null
    return this.active_skinned_mesh.skeleton
  }

  enter (): void {
    if (this.active) return
    this.active = true

    // Snapshot whether the actions were playing so we can restore on exit.
    this.was_playing_before_enter = this.active_actions.some(a => a.isRunning() && a.paused !== true)
    for (const action of this.active_actions) action.paused = true

    this.transform_controls.setMode('rotate')
    this.transform_controls.enabled = true
  }

  exit (): void {
    if (!this.active) return
    this.active = false

    // Clear the selection through the same channel listeners subscribed to, so
    // side-effects (highlighter, picker UI state) get a deselect signal.
    this.select_bone(null)

    this.transform_controls.enabled = false

    if (this.was_playing_before_enter) {
      for (const action of this.active_actions) action.paused = false
    }
    this.was_playing_before_enter = false
  }

  toggle (): boolean {
    if (this.active) this.exit()
    else this.enter()
    return this.active
  }
}
